import { LinkedinLogo } from "@phosphor-icons/react";

import {
  MobileSelectionListItem,
  PaginatedMobileSelectionList,
} from "common/components/MobileSelectionList";
import ProfileImage from "common/components/ui/ProfileImage";
import Skeleton from "common/components/ui/Skeleton";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { getProfileSubtitle } from "common/helpers/utils";

import { CREATED_CAMPAIGN_AUDIENCE_COLUMNS } from "../../../../../constants";

import type { Dispatch, SetStateAction } from "react";
import type { AudienceRow } from "../../../../../types";

interface AudienceMobileListProps {
  rows: AudienceRow[];
  isLoading: boolean;
  selectedTargets: AudienceRow[];
  setSelectedTargets: Dispatch<SetStateAction<AudienceRow[]>>;
}

export default function AudienceMobileList({
  rows,
  isLoading,
  selectedTargets,
  setSelectedTargets,
}: AudienceMobileListProps) {
  const { permissions } = useSelectedWorkspaceContext();

  return (
    <PaginatedMobileSelectionList
      title="Targets"
      data={rows}
      localStorageId="campaign-details-audience"
      selection={selectedTargets}
      onSelectionChange={
        permissions.includes("campaigns_full")
          ? (newSelection) => {
              setSelectedTargets(newSelection === "all" ? rows : newSelection);
            }
          : undefined
      }
      allItems={CREATED_CAMPAIGN_AUDIENCE_COLUMNS}
    >
      {(row, listItemProps) => (
        <MobileSelectionListItem
          {...listItemProps}
          relativeTo={
            permissions.includes("leads_full") ? `leads/${row.id}` : undefined
          }
        >
          {isLoading ? (
            <Skeleton className="size-8 shrink-0 rounded-full" />
          ) : (
            <ProfileImage size="sm" src={row.picture_url} />
          )}

          <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-left">
            {isLoading ? (
              <Skeleton className="h-4 w-28" />
            ) : (
              <>
                <div className="flex items-center gap-x-1">
                  <span className="truncate text-sm-bold">{row.full_name}</span>

                  {row.premium && (
                    <LinkedinLogo
                      className="size-4 shrink-0 text-yellow-500"
                      weight="fill"
                    />
                  )}
                </div>

                <span className="line-clamp-1 text-xs-medium text-black-500">
                  {getProfileSubtitle(row)}
                </span>
              </>
            )}
          </div>
        </MobileSelectionListItem>
      )}
    </PaginatedMobileSelectionList>
  );
}
